import React, { useState } from 'react';
import { Navigate, Link, useLocation, Outlet } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useTenant from '../../hooks/useTenant';
import { useNavigation } from '../../hooks/useNavigation';
import { AppLayoutSkeleton } from '../ui/UiComponents';

export const ProtectedLayout: React.FC = () => {
  const { user, isAuthenticated, loading, logout, hasRole } = useAuth();
  const { tenantName, logoUrl } = useTenant();
  const location = useLocation();

  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const activeRoles = user?.roles || [];
  const { getNavItems } = useNavigation();

  if (loading) {
    return <AppLayoutSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const navItems = getNavItems();
  const bottomNavSpecs = [
    { path: '/dashboard', label: 'Home' },
    { path: '/attendance', label: 'Register' },
    { path: '/my-class-list', label: 'Pupils' },
    { path: '/reports', label: 'Reports' }
  ];

  const bottomNavItems = bottomNavSpecs
    .map(spec => {
      const original = navItems.find(item => item.path === spec.path);
      return original ? { ...original, label: spec.label } : null;
    })
    .filter(Boolean) as typeof navItems;

  const isDashboard = location.pathname === '/dashboard';

  const initials = user
    ? `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase()
    : 'U';

  const isNavItemActive = (currentPath: string, navPath: string) => {
    let active = currentPath === navPath || (navPath !== '/' && currentPath.startsWith(navPath));

    // Overrides for sub-pages mapped under /classes
    if (currentPath.includes('/attendance')) {
      active = navPath === '/attendance';
    } else if (currentPath.match(/^\/classes\/[^\/]+$/)) {
      active = navPath === '/my-class-list';
    }

    return active;
  };

  const currentNav = navItems.find(item => isNavItemActive(location.pathname, item.path));
  const breadcrumbLabel = currentNav ? currentNav.label : 'Application';

  const userRolesDisplay = activeRoles
    ? activeRoles.map((r: string) => r.replace(/_/g, ' ')).join(', ')
    : 'User';

  const renderSidebarLinks = (onClickCallback?: () => void) => {
    const groupedItems: Record<string, typeof navItems> = {
      'MAIN': [],
      'ACADEMIC': [],
      'SETUP': []
    };
    const otherItems: typeof navItems = [];

    navItems.forEach(item => {
      if (item.group && groupedItems[item.group]) {
        groupedItems[item.group].push(item);
      } else {
        otherItems.push(item);
      }
    });

    const renderGroup = (groupName: string, items: typeof navItems) => {
      if (items.length === 0) return null;
      return (
        <div key={groupName} className="mb-6">
          <div className={`px-4 mb-2 text-[11px] font-bold tracking-widest uppercase text-slate-400 transition-all duration-200 ${!sidebarExpanded ? 'lg:hidden' : 'block'}`}>
            {groupName}
          </div>
          <div className="space-y-1">
            {items.map((item) => {
              const active = isNavItemActive(location.pathname, item.path);
              
              const itemClass = active
                ? 'bg-indigo-50/80 text-indigo-700 font-semibold shadow-sm border border-indigo-100/50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100';
              const iconClass = active ? 'text-indigo-600' : 'text-slate-400';

              return (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={onClickCallback}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-3 px-3 py-2.5 mx-2 rounded-lg transition-all duration-200 text-[13.5px] font-medium active:scale-[0.98] ${itemClass}`}
                >
                  <span className={`shrink-0 [&>svg]:w-[18px] [&>svg]:h-[18px] flex items-center justify-center ${iconClass} ${!sidebarExpanded ? 'mx-auto' : ''}`}>{item.icon}</span>
                  <span className={`transition-opacity duration-200 ${!sidebarExpanded ? 'lg:hidden' : ''}`}>
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <>
        {renderGroup('MAIN', groupedItems['MAIN'])}
        {renderGroup('ACADEMIC', groupedItems['ACADEMIC'])}
        {renderGroup('SETUP', groupedItems['SETUP'])}
        {otherItems.length > 0 && renderGroup('OTHER', otherItems)}
      </>
    );
  };

  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  };

  const formattedDate = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-slate-50 text-slate-900">
      
      {/* Sticky Header with User Context */}
      <header className={`sticky top-0 z-30 bg-white border-b border-slate-200 shrink-0 w-full transition-all duration-300
        ${isDashboard ? 'hidden md:flex' : 'flex flex-col md:flex-row'} 
        px-6 pt-12 pb-6 rounded-b-[2.5rem] 
        md:h-20 md:px-8 md:pt-0 md:pb-0 md:rounded-none md:items-center md:justify-between`}
      >
        {/* Desktop Left Side: Logo + School Name / Breadcrumb */}
        <div className="hidden md:flex items-center gap-3">
          <Link to="/dashboard" className="flex items-center shrink-0 transition-transform hover:scale-105">
            {logoUrl ? (
              <img src={logoUrl} alt={tenantName} className="h-8 w-auto max-w-[36px] rounded-md object-contain" />
            ) : (
              <img src={process.env.PUBLIC_URL + '/logo.png'} alt={tenantName || 'System Logo'} className="h-8 w-auto max-w-[36px] rounded-md object-contain" />
            )}
          </Link>
          <div className="flex items-center gap-2 text-[14px] font-semibold text-slate-500">
            <Link to="/dashboard" className="text-slate-900 font-bold hover:text-indigo-600 transition-colors">
              {tenantName}
            </Link>
            <span className="text-slate-300">/</span>
            <span className="text-slate-600 font-semibold">{breadcrumbLabel}</span>
          </div>
        </div>

        {/* Right side & Mobile Layout wrapper */}
        <div className="flex items-start justify-between w-full md:w-auto md:items-center md:gap-4">
          
          {/* Mobile Title with Logo (Hidden on Desktop) */}
          <div className="md:hidden flex items-center gap-3 mt-1">
            <Link to="/dashboard" className="shrink-0 transition-transform hover:scale-105">
              {logoUrl ? (
                <img src={logoUrl} alt={tenantName} className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 p-1 object-contain shadow-sm" />
              ) : (
                <img src={process.env.PUBLIC_URL + '/logo.png'} alt={tenantName || 'System Logo'} className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 p-1 object-contain shadow-sm" />
              )}
            </Link>
            <div>
              <p className="text-slate-500 text-[13px] font-medium truncate max-w-[180px]">{tenantName || 'School Management'}</p>
              <h1 className="text-xl font-bold mt-0.5 text-slate-900 tracking-tight">{breadcrumbLabel}</h1>
            </div>
          </div>

          {/* User Meta Card with Dropdown */}
          <div className="flex items-center md:pl-5 relative">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setUserMenuOpen(!userMenuOpen)}>
              <div className="text-right hidden sm:block">
                <p className="font-bold text-[13px] text-slate-900 leading-tight uppercase tracking-wide">
                  {user?.first_name} {user?.last_name}
                </p>
                <span className="text-[11px] text-indigo-600 font-bold uppercase tracking-widest block mt-0.5">
                  {activeRoles?.[0]?.replace(/_/g, ' ') || 'User'}
                </span>
              </div>
              <div className="w-9 h-9 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-[13px] shrink-0">
                {initials}
              </div>
              <svg className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                ></div>
                <div className="absolute right-0 top-14 md:top-12 w-56 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-slate-100 z-50 py-1 animate-fade-in-up">
                  <div className="px-4 py-2 border-b border-slate-100 sm:hidden">
                    <p className="font-bold text-sm text-slate-800 truncate">{user?.first_name} {user?.last_name}</p>
                    <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider truncate">{userRolesDisplay}</p>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout();
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 transition-colors text-left"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* 1. Desktop Sidebar Navigation */}
        {!isDashboard && (
          <aside className={`hidden md:flex flex-col bg-white text-slate-800 shrink-0 shadow-lg border-r border-slate-200 transition-all duration-200 z-20 ${sidebarExpanded ? 'w-64' : 'w-20'}`}>
            <nav className="flex-1 overflow-y-auto py-6 sidebar-scrollbar">
              {renderSidebarLinks()}
            </nav>
            <div className="p-3 border-t border-slate-200 space-y-1">
              <button
                onClick={() => setSidebarExpanded(!sidebarExpanded)}
                className="w-full flex items-center justify-center py-2.5 rounded-lg text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all text-helper font-medium gap-2"
              >
                <svg className={`w-4 h-4 transition-transform duration-200 ${sidebarExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
                <span className={sidebarExpanded ? 'inline' : 'hidden'}>Collapse Sidebar</span>
              </button>
            </div>
          </aside>
        )}

        {/* 2. Mobile Drawer Navigation Overlay */}
        {mobileDrawerOpen && (
          <div className="fixed inset-0 z-40 md:hidden bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300" onClick={() => setMobileDrawerOpen(false)}>
            <aside className="w-72 max-w-[85vw] h-full bg-white text-slate-800 flex flex-col justify-between py-6 shadow-2xl animate-slide-in" onClick={e => e.stopPropagation()}>
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center gap-3 px-5 mb-6 border-b border-slate-200 pb-5 shrink-0">
                  {logoUrl ? (
                    <img src={logoUrl} alt={tenantName} className="w-9 h-9 rounded-lg bg-white p-0.5 object-cover" />
                  ) : (
                    <img src={process.env.PUBLIC_URL + '/logo.png'} alt={tenantName || 'System Logo'} className="w-9 h-9 rounded-lg bg-white p-0.5 object-cover" />
                  )}
                  <div>
                    <h2 className="font-bold text-[14px] text-slate-800 leading-snug line-clamp-2 max-w-[180px]">{tenantName}</h2>
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block mt-0.5">T-file Mobile</span>
                  </div>
                </div>
                <nav className="flex flex-col gap-1.5 px-3 flex-1 overflow-y-auto sidebar-scrollbar">
                  {renderSidebarLinks(() => setMobileDrawerOpen(false))}
                </nav>
              </div>
              <div className="px-5 border-t border-slate-200 pt-5 shrink-0">
                <button
                  onClick={() => { setMobileDrawerOpen(false); logout(); }}
                  className="w-full flex items-center gap-3 py-3 text-rose-400 font-semibold text-helper"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Sign Out</span>
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* 3. Main Interface Panel Workspace */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Main Content Pane */}
          <div className="flex-1 min-h-0">
            <main className="h-full overflow-y-auto p-4 md:p-8 pb-20 md:pb-8 bg-slate-50">
              <Outlet />
            </main>
          </div>

          {/* Mobile Bottom Navigation Bar */}
          <nav className="fixed bottom-0 left-0 right-0 z-30 md:hidden bg-white/95 backdrop-blur-md border-t border-slate-200 rounded-t-3xl h-[76px] flex items-center justify-around px-2 shadow-[0_-10px_30px_-15px_rgba(0,0,0,0.1)] pb-safe pt-2">
            {bottomNavItems.map((item) => {
              const active = isNavItemActive(location.pathname, item.path);

              return (
                <Link
                  key={item.label}
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  className={`relative flex flex-col items-center justify-center w-[64px] h-[56px] transition-all duration-300 active:scale-95 ${active
                    ? 'text-indigo-600'
                    : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  <span className={`[&>svg]:w-[22px] [&>svg]:h-[22px] mb-1.5 transition-transform duration-300 ${active ? '-translate-y-0.5 text-indigo-600' : 'text-slate-500'}`}>{item.icon}</span>
                  <span className={`text-[10px] font-semibold tracking-wide leading-none text-center transition-all duration-300 ${active ? 'text-indigo-600' : 'text-slate-500'}`}>{item.label}</span>
                  {active && (
                    <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-indigo-600"></span>
                  )}
                </Link>
              );
            })}

            <button
              onClick={() => setMobileDrawerOpen(true)}
              className="relative flex flex-col items-center justify-center w-[64px] h-[56px] transition-all duration-300 active:scale-95 text-slate-500 hover:text-slate-700 border-none bg-transparent p-0"
            >
              <span className="[&>svg]:w-[22px] [&>svg]:h-[22px] mb-1.5 text-slate-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </span>
              <span className="text-[10px] font-semibold tracking-wide leading-none text-center">More</span>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default ProtectedLayout;
